import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { assignAdminSensorOrderDevices as assignAdminSensorOrderDevicesService } from 'scenes/Renolog/services/assignAdminSensorOrderDevices.service';
import { cancelAdminSensorOrder as cancelAdminSensorOrderService } from 'scenes/Renolog/services/cancelAdminSensorOrder.service';
import { confirmAdminSensorOrder as confirmAdminSensorOrderService } from 'scenes/Renolog/services/confirmAdminSensorOrder.service';
import { getAdminDevices } from 'scenes/Renolog/services/getAdminDevices.service';
import { getAdminEmployerProjects } from 'scenes/Renolog/services/getAdminEmployerProjects.service';
import { getAdminSensorOrders } from 'scenes/Renolog/services/getAdminSensorOrders.service';
import { patchAdminDevice as patchAdminDeviceService } from 'scenes/Renolog/services/patchAdminDevice.service';
import { patchAdminSensorOrder as patchAdminSensorOrderService } from 'scenes/Renolog/services/patchAdminSensorOrder.service';
import { setAdminSensorOrderReady as setAdminSensorOrderReadyService } from 'scenes/Renolog/services/setAdminSensorOrderReady.service';
import { syncAdminDevices } from 'scenes/Renolog/services/syncAdminDevices.service';
import { getNotificationEmails, updateNotificationEmails } from 'scenes/Renolog/services/notificationEmails.service';
import { getAdminRenologPricing } from 'scenes/Renolog/services/getAdminRenologPricing.service';
import { updateAdminRenologPricing } from 'scenes/Renolog/services/updateAdminRenologPricing.service';

export const cancelAdminSensorOrder = createAsyncThunk(
  'renologAdmin/cancelAdminSensorOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const order = await cancelAdminSensorOrderService(orderId);
      return { orderId, order };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to cancel order');
    }
  }
);

export const confirmAdminSensorOrder = createAsyncThunk(
  'renologAdmin/confirmAdminSensorOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const order = await confirmAdminSensorOrderService(orderId);
      return order;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to confirm order');
    }
  }
);

export const patchAdminSensorOrder = createAsyncThunk(
  'renologAdmin/patchAdminSensorOrder',
  async ({ orderId, orderData }, { rejectWithValue }) => {
    try {
      const order = await patchAdminSensorOrderService(orderId, orderData);
      return order;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update order');
    }
  }
);

export const assignAdminSensorOrder = createAsyncThunk(
  'renologAdmin/assignAdminSensorOrder',
  async ({ orderId, deviceIds, assignedAt }, { rejectWithValue }) => {
    try {
      const order = await assignAdminSensorOrderDevicesService(
        orderId,
        deviceIds,
        assignedAt
      );
      return order;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to assign devices');
    }
  }
);

export const setAdminSensorOrderReady = createAsyncThunk(
  'renologAdmin/setAdminSensorOrderReady',
  async (orderId, { rejectWithValue }) => {
    try {
      const order = await setAdminSensorOrderReadyService(orderId);
      return order;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to set order ready');
    }
  }
);

export const fetchAdminSensorOrders = createAsyncThunk(
  'renologAdmin/fetchAdminSensorOrders',
  async (_, { rejectWithValue }) => {
    try {
      const orders = await getAdminSensorOrders();
      return orders;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch admin sensor orders'
      );
    }
  }
);

export const fetchAdminDevices = createAsyncThunk(
  'renologAdmin/fetchAdminDevices',
  async (_, { rejectWithValue }) => {
    try {
      const devices = await getAdminDevices();
      return devices;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch admin devices');
    }
  }
);

export const patchAdminDevice = createAsyncThunk(
  'renologAdmin/patchAdminDevice',
  async ({ deviceId, deviceData }, { rejectWithValue }) => {
    try {
      const device = await patchAdminDeviceService(deviceId, deviceData);
      return device;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update device');
    }
  }
);

export const syncDevices = createAsyncThunk(
  'renologAdmin/syncDevices',
  async (_, { rejectWithValue }) => {
    try {
      const devices = await syncAdminDevices();
      return devices;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to sync devices');
    }
  }
);

export const fetchAdminEmployerProjects = createAsyncThunk(
  'renologAdmin/fetchAdminEmployerProjects',
  async (_, { rejectWithValue }) => {
    try {
      const projects = await getAdminEmployerProjects();
      return projects;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch employer projects'
      );
    }
  }
);

export const fetchNotificationEmails = createAsyncThunk(
  'renologAdmin/fetchNotificationEmails',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getNotificationEmails();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch notification emails'
      );
    }
  }
);

export const saveNotificationEmails = createAsyncThunk(
  'renologAdmin/saveNotificationEmails',
  async (emails, { rejectWithValue }) => {
    try {
      const data = await updateNotificationEmails(emails);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to save notification emails'
      );
    }
  }
);

export const fetchAdminRenologPricing = createAsyncThunk(
  'renologAdmin/fetchAdminRenologPricing',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAdminRenologPricing();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch pricing'
      );
    }
  }
);

export const saveAdminRenologPricing = createAsyncThunk(
  'renologAdmin/saveAdminRenologPricing',
  async (pricingData, { rejectWithValue }) => {
    try {
      const data = await updateAdminRenologPricing(pricingData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to save pricing'
      );
    }
  }
);

const renologAdminSlice = createSlice({
  name: 'renologAdmin',
  initialState: {
    orders: [],
    loading: false,
    loadingStarted: false,
    error: undefined,
    devices: [],
    devicesLoading: false,
    devicesLoadingStarted: false,
    devicesError: undefined,
    devicesSyncing: false,
    devicesSyncError: undefined,
    deviceSaving: false,
    deviceSaveError: undefined,
    employerProjects: [],
    employerProjectsLoading: false,
    employerProjectsLoadingStarted: false,
    notificationEmails: [],
    notificationEmailsLoading: false,
    notificationEmailsLoadingStarted: false,
    notificationEmailsSaving: false,
    notificationEmailsError: undefined,
    pricing: null,
    pricingLoading: false,
    pricingLoadingStarted: false,
    pricingSaving: false,
    pricingError: undefined,
    orderActionPending: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminSensorOrders.pending, (state) => {
        state.loading = true;
        state.loadingStarted = true;
        state.error = undefined;
      })
      .addCase(fetchAdminSensorOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(fetchAdminSensorOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Unknown error';
      })
      .addCase(fetchAdminDevices.pending, (state) => {
        state.devicesLoading = true;
        state.devicesLoadingStarted = true;
        state.devicesError = undefined;
      })
      .addCase(fetchAdminDevices.fulfilled, (state, action) => {
        state.devicesLoading = false;
        state.devices = action.payload;
      })
      .addCase(fetchAdminDevices.rejected, (state, action) => {
        state.devicesLoading = false;
        state.devicesError = action.payload || 'Unknown error';
      })
      .addCase(syncDevices.pending, (state) => {
        state.devicesSyncing = true;
        state.devicesSyncError = undefined;
      })
      .addCase(syncDevices.fulfilled, (state, action) => {
        state.devicesSyncing = false;
        state.devices = action.payload;
      })
      .addCase(syncDevices.rejected, (state, action) => {
        state.devicesSyncing = false;
        state.devicesSyncError = action.payload || 'Unknown error';
      })
      .addCase(patchAdminDevice.pending, (state) => {
        state.deviceSaving = true;
        state.deviceSaveError = undefined;
      })
      .addCase(patchAdminDevice.fulfilled, (state, action) => {
        state.deviceSaving = false;
        const updated = action.payload;
        const idx = state.devices.findIndex(
          (d) => d.deviceId === updated.deviceId
        );
        if (idx !== -1) {
          state.devices[idx] = updated;
        }
      })
      .addCase(patchAdminDevice.rejected, (state, action) => {
        state.deviceSaving = false;
        state.deviceSaveError = action.payload || 'Unknown error';
      })
      .addCase(fetchAdminEmployerProjects.pending, (state) => {
        state.employerProjectsLoading = true;
        state.employerProjectsLoadingStarted = true;
      })
      .addCase(fetchAdminEmployerProjects.fulfilled, (state, action) => {
        state.employerProjectsLoading = false;
        state.employerProjects = action.payload;
      })
      .addCase(fetchAdminEmployerProjects.rejected, (state) => {
        state.employerProjectsLoading = false;
      })
      .addCase(cancelAdminSensorOrder.pending, (state) => {
        state.orderActionPending = true;
      })
      .addCase(cancelAdminSensorOrder.fulfilled, (state, action) => {
        state.orderActionPending = false;
        const { orderId, order } = action.payload;
        const idx = state.orders.findIndex((o) => o.id === orderId);
        if (idx !== -1) {
          state.orders[idx] = order ?? state.orders[idx];
        }
      })
      .addCase(cancelAdminSensorOrder.rejected, (state) => {
        state.orderActionPending = false;
      })
      .addCase(confirmAdminSensorOrder.pending, (state) => {
        state.orderActionPending = true;
      })
      .addCase(confirmAdminSensorOrder.fulfilled, (state, action) => {
        state.orderActionPending = false;
        const updated = action.payload;
        if (updated) {
          const idx = state.orders.findIndex((o) => o.id === updated.id);
          if (idx !== -1) state.orders[idx] = updated;
        }
      })
      .addCase(confirmAdminSensorOrder.rejected, (state) => {
        state.orderActionPending = false;
      })
      .addCase(patchAdminSensorOrder.pending, (state) => {
        state.orderActionPending = true;
      })
      .addCase(patchAdminSensorOrder.fulfilled, (state, action) => {
        state.orderActionPending = false;
        const updated = action.payload;
        if (updated) {
          const idx = state.orders.findIndex((o) => o.id === updated.id);
          if (idx !== -1) state.orders[idx] = updated;
        }
      })
      .addCase(patchAdminSensorOrder.rejected, (state) => {
        state.orderActionPending = false;
      })
      .addCase(assignAdminSensorOrder.pending, (state) => {
        state.orderActionPending = true;
      })
      .addCase(assignAdminSensorOrder.fulfilled, (state, action) => {
        state.orderActionPending = false;
        const { updatedOrder, updatedDevices } = action.payload ?? {};
        if (updatedOrder) {
          const idx = state.orders.findIndex((o) => o.id === updatedOrder.id);
          if (idx !== -1) state.orders[idx] = updatedOrder;
        }
        if (Array.isArray(updatedDevices)) {
          updatedDevices.forEach((device) => {
            const idx = state.devices.findIndex(
              (d) => d.deviceId === device.deviceId
            );
            if (idx !== -1) {
              state.devices[idx] = device;
            }
          });
        }
      })
      .addCase(assignAdminSensorOrder.rejected, (state) => {
        state.orderActionPending = false;
      })
      .addCase(setAdminSensorOrderReady.pending, (state) => {
        state.orderActionPending = true;
      })
      .addCase(setAdminSensorOrderReady.fulfilled, (state, action) => {
        state.orderActionPending = false;
        const updated = action.payload;
        if (updated) {
          const idx = state.orders.findIndex((o) => o.id === updated.id);
          if (idx !== -1) state.orders[idx] = updated;
        }
      })
      .addCase(setAdminSensorOrderReady.rejected, (state) => {
        state.orderActionPending = false;
      })
      .addCase(fetchNotificationEmails.pending, (state) => {
        state.notificationEmailsLoading = true;
        state.notificationEmailsLoadingStarted = true;
        state.notificationEmailsError = undefined;
      })
      .addCase(fetchNotificationEmails.fulfilled, (state, action) => {
        state.notificationEmailsLoading = false;
        state.notificationEmails = action.payload.notificationEmails || [];
      })
      .addCase(fetchNotificationEmails.rejected, (state, action) => {
        state.notificationEmailsLoading = false;
        state.notificationEmailsError = action.payload || 'Unknown error';
      })
      .addCase(saveNotificationEmails.pending, (state) => {
        state.notificationEmailsSaving = true;
        state.notificationEmailsError = undefined;
      })
      .addCase(saveNotificationEmails.fulfilled, (state, action) => {
        state.notificationEmailsSaving = false;
        state.notificationEmails = action.payload.notificationEmails || [];
      })
      .addCase(saveNotificationEmails.rejected, (state, action) => {
        state.notificationEmailsSaving = false;
        state.notificationEmailsError = action.payload || 'Unknown error';
      })
      .addCase(fetchAdminRenologPricing.pending, (state) => {
        state.pricingLoading = true;
        state.pricingLoadingStarted = true;
        state.pricingError = undefined;
      })
      .addCase(fetchAdminRenologPricing.fulfilled, (state, action) => {
        state.pricingLoading = false;
        state.pricing = action.payload;
      })
      .addCase(fetchAdminRenologPricing.rejected, (state, action) => {
        state.pricingLoading = false;
        state.pricingError = action.payload || 'Unknown error';
      })
      .addCase(saveAdminRenologPricing.pending, (state) => {
        state.pricingSaving = true;
        state.pricingError = undefined;
      })
      .addCase(saveAdminRenologPricing.fulfilled, (state, action) => {
        state.pricingSaving = false;
        state.pricing = action.payload;
      })
      .addCase(saveAdminRenologPricing.rejected, (state, action) => {
        state.pricingSaving = false;
        state.pricingError = action.payload || 'Unknown error';
      });
  },
});

export const selectAdminSensorOrders = (state) => state.renologAdmin.orders;
export const selectAdminSensorOrdersLoading = (state) =>
  state.renologAdmin.loading;
export const selectAdminSensorOrdersLoadingStarted = (state) =>
  state.renologAdmin.loadingStarted;

export const selectAdminDevices = (state) => state.renologAdmin.devices;
export const selectAdminDevicesLoading = (state) =>
  state.renologAdmin.devicesLoading;
export const selectAdminDevicesLoadingStarted = (state) =>
  state.renologAdmin.devicesLoadingStarted;
export const selectAdminDevicesSyncing = (state) =>
  state.renologAdmin.devicesSyncing;
export const selectAdminDeviceSaving = (state) =>
  state.renologAdmin.deviceSaving;

export const selectAdminEmployerProjects = (state) =>
  state.renologAdmin.employerProjects;
export const selectAdminEmployerProjectsLoading = (state) =>
  state.renologAdmin.employerProjectsLoading;
export const selectAdminEmployerProjectsLoadingStarted = (state) =>
  state.renologAdmin.employerProjectsLoadingStarted;

export const selectAdminOrderActionPending = (state) =>
  state.renologAdmin.orderActionPending;

export const selectNotificationEmails = (state) =>
  state.renologAdmin.notificationEmails;
export const selectNotificationEmailsLoading = (state) =>
  state.renologAdmin.notificationEmailsLoading;
export const selectNotificationEmailsLoadingStarted = (state) =>
  state.renologAdmin.notificationEmailsLoadingStarted;
export const selectNotificationEmailsSaving = (state) =>
  state.renologAdmin.notificationEmailsSaving;
export const selectNotificationEmailsError = (state) =>
  state.renologAdmin.notificationEmailsError;

export const selectAdminRenologPricing = (state) =>
  state.renologAdmin.pricing;
export const selectAdminRenologPricingLoading = (state) =>
  state.renologAdmin.pricingLoading;
export const selectAdminRenologPricingLoadingStarted = (state) =>
  state.renologAdmin.pricingLoadingStarted;
export const selectAdminRenologPricingSaving = (state) =>
  state.renologAdmin.pricingSaving;
export const selectAdminRenologPricingError = (state) =>
  state.renologAdmin.pricingError;

export default renologAdminSlice.reducer;
